import { useEffect, useMemo } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import AccessShell from "../components/AccessShell.jsx";
import SidebarLayout from "../components/SidebarLayout.jsx";
import { useFeedback } from "../components/FeedbackProvider.jsx";
import { roleCanAccessSection, SECTION_NAV } from "../constants.js";
import { cognitoConfig } from "../auth/config.js";
import useCampaigns from "../hooks/useCampaigns.js";
import useDashboardMetrics from "../hooks/useDashboardMetrics.js";
import useHistory from "../hooks/useHistory.js";
import useUserManagement from "../hooks/useUserManagement.js";
import { getCustomerAuthErrorMessage } from "../utils/customerMessages.js";

const COLOGUARD_GROUP_ROLES = [
  ["cologuard-admin", "admin"],
  ["cologuard-manager", "manager"],
  ["cologuard-viewer", "viewer"],
];

function normalizeGroups(groupsClaim) {
  if (Array.isArray(groupsClaim)) return groupsClaim;
  if (typeof groupsClaim === "string") return [groupsClaim];
  return [];
}

export default function CologuardPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const { notify } = useFeedback();
  const accessToken = auth.user?.access_token || "";
  const currentUser = useMemo(() => {
    if (!auth.user?.profile) return null;
    const profile = auth.user.profile;
    const groups = normalizeGroups(profile["cognito:groups"]);
    const role = COLOGUARD_GROUP_ROLES.find(([group]) => groups.includes(group))?.[1] || "";
    return {
      user_id: profile.sub,
      email: profile.email || "",
      name: profile.name || profile.given_name || profile.email || "Cologuard user",
      role,
      groups,
    };
  }, [auth.user]);

  const isAuthenticated = auth.isAuthenticated && Boolean(currentUser?.role);
  const isAdminUser = currentUser?.role === "admin";
  const canManageCampaigns = roleCanAccessSection(currentUser?.role, "setup");
  const availableSectionNav = useMemo(
    () => SECTION_NAV.filter((section) => section.roles.includes(currentUser?.role)),
    [currentUser?.role]
  );

  const history = useHistory({ isAuthenticated, accessToken });
  const campaigns = useCampaigns({
    isAuthenticated,
    currentUser,
    accessToken,
    notify,
    onCampaignQueued: () => navigate("/cologuard"),
  });
  const dashboardCampaignDesignators = useMemo(
    () => campaigns.campaignState.campaigns
      .map((campaign) => campaign.campaign_designator)
      .filter(Boolean),
    [campaigns.campaignState.campaigns]
  );
  const dashboardMetrics = useDashboardMetrics({
    isAuthenticated,
    accessToken,
    campaignDesignators: dashboardCampaignDesignators,
  });
  const users = useUserManagement({
    enabled: isAuthenticated && isAdminUser && location.pathname.endsWith("/users"),
    accessToken,
    notify,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  useEffect(() => {
    document.body.classList.add("cologuard-layout");
    return () => document.body.classList.remove("cologuard-layout");
  }, []);

  async function handleSignOut() {
    try {
      await auth.removeUser();
    } finally {
      const logoutUrl = new URL(`${cognitoConfig.domain}/logout`);
      logoutUrl.searchParams.set("client_id", cognitoConfig.clientId);
      logoutUrl.searchParams.set("logout_uri", cognitoConfig.logoutUri);
      window.location.assign(logoutUrl.toString());
    }
  }

  if (auth.isLoading || !auth.isAuthenticated || !currentUser?.role) {
    return (
      <div className="cologuard-phase2">
        <div className="cg-admin-shell">
          <AccessShell
            error={getCustomerAuthErrorMessage(auth.error)}
            isLoading={auth.isLoading || Boolean(auth.activeNavigator)}
            isUnauthorized={auth.isAuthenticated && !currentUser?.role}
            onSignIn={() => auth.signinRedirect()}
            onSignOut={handleSignOut}
          />
        </div>
      </div>
    );
  }

  const requestedSection = SECTION_NAV.find(
    (section) => section.key !== "summary" && location.pathname.endsWith(`/${section.key}`)
  );
  if (requestedSection && !requestedSection.roles.includes(currentUser.role)) {
    return <Navigate to="/cologuard" replace />;
  }

  return (
    <div className="cologuard-phase2">
      <div className="cg-admin-shell">
        <SidebarLayout currentUser={currentUser} sectionNav={availableSectionNav} onSignOut={handleSignOut}>
          <Outlet
            context={{
              accessToken,
              currentUser,
              isAdminUser,
              canManageCampaigns,
              ...history,
              ...dashboardMetrics,
              ...campaigns,
              ...users,
              isGeneratingMagento: campaigns.isSubmitting,
            }}
          />
        </SidebarLayout>
      </div>
    </div>
  );
}
